import { HttpClient } from '@angular/common/http';
import { Component, EventEmitter, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { lastValueFrom, map } from 'rxjs';
import { SharedModule } from 'src/app/layouts/full/shared/shared.module';
import { ChargeService } from 'src/app/services/charge.service';
import { ScanService } from 'src/app/services/scan.service';
import { UserService } from 'src/app/services/user.service';
import { environment } from 'src/environments/environment';
import Swal from 'sweetalert2';
import { ThreeDFileClassificatorComponent } from '../three-d-file-classificator/three-d-file-classificator.component';
import { Scan } from 'src/app/models/scan.model';
import JSZip from 'jszip';
import { WebsocketService } from 'src/app/services/post-process-websocket.service';
import { saveAs } from 'file-saver';
import { FileUpload } from 'primeng/fileupload';

const BASE_URL = environment.base_url;

@Component({
  selector: 'app-files',
  standalone: true,
  imports: [SharedModule, ThreeDFileClassificatorComponent],
  templateUrl: './files.component.html',
  styleUrl: './files.component.scss',
  providers: [MessageService]
})
export class FilesComponent {
  public orderId: string;
  public paid: boolean;
  public files = [];
  public loading: boolean = false;
  filesFormData = []
  selectedOrder: Scan = null
  filesToClassify = []
  showFileClassificationModal: boolean = false
  selectedFiles: any[] = [];
  order: Scan = null

  @ViewChild('fileUpload') fileUpload: FileUpload;

  public orderFileAccessChanged: EventEmitter<string> = new EventEmitter<string>();

  // Evento específico para indicar que un archivo ha sido pagado
  public filePaidOccurred = new EventEmitter<string>();

  constructor(
    private chargeService: ChargeService,
    private scanService: ScanService,
    private router: Router,
    private messageService: MessageService,
    private http: HttpClient,
    private userService: UserService,
    private route: ActivatedRoute,
    public webSocket: WebsocketService,
    private translate: TranslateService
  ) {
    this.orderFileAccessChanged.subscribe((file) => {
      this.loadFiles();
    })
  }

  async ngOnInit() {
    this.webSocket.connectDefault();
    this.webSocket.subject.subscribe(
      (msg: any) => {
        this.loadFiles()
        console.log("Message received on files component:", msg)
      }, // Called whenever there is a message from the server.
      err => console.log("error receiving" + err), // Called if at any point WebSocket API signals some kind of error.
      () => {
        console.log('complete'), // Called when connection is closed (for whatever reason) and reconnect.
          this.webSocket.connectDefault()
      }
    );
    this.orderId = this.route.snapshot.paramMap.get("orderId")
    this.order = await lastValueFrom(this.scanService.getOne(this.orderId));
    this.loadFiles()
  }

  loadFiles() {
    this.loading = true;

    const url = `${BASE_URL}/files/${this.orderId}`;
    const token = localStorage.getItem('token') || '';

    const headers = {
      'X-Amz-Security-Token': token
    }

    this.http.get(url, { headers }).pipe(
      map((resp: { filesFound }) => {
        return resp.filesFound.map(file => {
          file.name = file.name.replace(this.orderId + "_", "");

          // Asignar categoría con prioridad
          if (file.name.includes('design')) {
            file.category = 'Designs';
            file.categoryPriority = "b";
          } else if (file.type === 'stl' || file.type === 'obj' || (file.type === 'ply' && file.name.includes('processed'))) {
            file.category = 'Scans';
            file.categoryPriority = "a";
          } else if (['pdf'].includes(file.type)) {
            file.category = 'PDFs';
            file.categoryPriority = "c";
          } else if (['jpg', 'jpeg', 'png'].includes(file.type)) {
            file.category = 'Images';
            file.categoryPriority = "d";
          }

          return file;
        });
      }),
      map((files) => {
        return files
          .filter(file => file.type !== 'ply' || (file.type === 'ply' && file.name.includes('processed')))
      })
    ).subscribe((files) => {
      this.loading = false;
      this.files = files;
      console.log("Files: ", files);
    });
  }


  emitFilePaid() {
    // Emite el evento `filePaid` cuando un archivo es pagado
    this.filePaidOccurred.emit(this.orderId);
  }

  downloadFile(fileUrl: string) {
    const parts = fileUrl.split('/');
    const encodedParts = parts.map((part, index) => {
      if (index === parts.length - 1) {
        return encodeURIComponent(part);
      }
      return part;
    });

    const safeUrl = encodedParts.join('/');
    window.open(safeUrl);
  }

  redirectTo3DViewer(orderId, fileName) {
    this.router.navigateByUrl(`/viewer/${orderId}/${fileName}`)
  }

  getAccessToFiles(filePath: string) {
    Swal.fire({
      title: this.translate.instant('MVF_REQUESTING_ACCESS'),
      text: this.translate.instant('MVF_WAIT_ACCESS')
    })
    Swal.showLoading();


    this.chargeService.charge(this.orderId, filePath).subscribe(
      () => {
        this.orderFileAccessChanged.emit('order_updated');
        this.emitFilePaid()
        Swal.fire({
          title: this.translate.instant('MVF_ACCESS_SUCCESS'),
          text: this.translate.instant('MVF_DOWNLOAD_NOW')
        })
        // this.modalViewFilesService.paid = true;
      }, (err) => {
        Swal.fire(err.error.message)
      }
    )

  }

  createZipFile(): Promise<Blob> {
    return new Promise<Blob>((resolve, reject) => {
      const zip = new JSZip();

      // Función para obtener la extensión del archivo
      const getFileExtension = (fileName: string): string => {
        return fileName.split('.').pop().toLowerCase(); // Obtener la extensión en minúsculas
      };

      // Función para generar nombres personalizados para los archivos
      const generateFileName = (file: File, index): string => {
        console.log("FILE IN GenerateFileName: ", file)
        console.log("This.filesFormData antes del index: ", this.filesFormData)
        const fileData = this.filesFormData[index]
        console.log("FileData: ", fileData)

        if (!fileData || !fileData.type) {
          console.warn(`Invalid fileFormData at index ${index}. Skipping this file.`);
          return null;
        }

        const type = fileData.type
        const fileExtension = getFileExtension(file.name);
        console.log("File extension: ", fileExtension)

        if (fileExtension === 'stl' || fileExtension === 'obj') {
          const name = this.selectedOrder.data['Patient Data'].name
          const surname = this.selectedOrder.data['Patient Data'].surname

          // Incluir el 'side' solo si está presente en fileData
          const sidePart = fileData.side ? `_${fileData.side}` : '';

          // Generar el nombre con type y side (si está presente)
          return `${name}_${surname}_${type}${sidePart}.${fileExtension}`;
        } else {
          return `${file.name}`
        }
      };

      // Agregar archivos seleccionados al zip
      this.files.forEach((file, index) => {
        const fileName = generateFileName(file, index);
        if (fileName) {
          zip.file(fileName, file);
        }
      });

      // Generar el archivo zip y resolver la promesa
      zip.generateAsync({ type: 'blob' })
        .then((blob) => resolve(blob))
        .catch((err) => reject(err));
    });
  }

  onFileSelect(event) {
    this.filesFormData = []

    // Convertir FileList a array
    const files: File[] = Array.from(event.files);

    // Añadir los archivos seleccionados a this.files sin duplicados
    this.files = [...files]

    // Crear un nuevo array para almacenar los archivos que necesitan ser clasificados
    const filesToClassify = [];

    for (const item of files) {
      const extension = item.name.split('.').pop().toLowerCase(); // Obtener extensión en minúsculas
      if (extension === 'stl' || extension === 'obj') {
        // Si el archivo es STL u OBJ, se añade a la lista de archivos a clasificar
        filesToClassify.push({
          name: item.name,
          extension
        });

        this.showFileClassificationModal = true;
      } else {
        // Si el archivo no es STL ni OBJ, añadirlo directamente a `filesFormData` después de filtrar por extensión
        const nonClassifiedFile = {
          name: item.name,
          extension,
          type: extension === "png" || "jpg" || "jpeg" ? "image" : "pdf"
        };

        // Asignamos el formato adecuado a `this.filesFormData`
        //@ts-expect-error
        this.filesFormData = { files: [nonClassifiedFile] };

        console.log("Formdata: ", this.filesFormData)
        this.handleFormDataReady(this.filesFormData)

      }
    }

    // Actualizar `filesToClassify` con los archivos nuevos
    this.filesToClassify = filesToClassify

  }

  syncS3FilesWithScan(orderId) {

    Swal.showLoading();

    this.scanService.syncS3fileToScan(orderId).subscribe((resp) => {

      Swal.close()

      this.orderFileAccessChanged.emit('order_updated')
    })
  }

  async uploadFile() {

    try {

      const signedZipUrlResponse = await lastValueFrom(this.scanService.getSignedZipUrl(this.orderId));
      const signedZipUrl = signedZipUrlResponse.signedZipUrl;

      if (!signedZipUrl) {
        Swal.fire(this.translate.instant('ERROR'), 'No signed URL available for upload', 'error');
        return;
      }

      // Esperar la respuesta de `getOne()`
      this.selectedOrder = await lastValueFrom(this.scanService.getOne(this.orderId));

      const blob = await this.createZipFile();

      Swal.update({
        title: this.translate.instant('MVF_UPLOADING_FILES'),
        showConfirmButton: false
      });
      Swal.showLoading();

      // Esperar la subida del archivo
      await lastValueFrom(this.scanService.uploadZipFile(signedZipUrl, blob));

      this.messageService.add({ severity: 'success', summary: this.translate.instant('MVF_SUCCESS'), detail: this.translate.instant('MVF_FILE_UPLOADED') });
      Swal.close();
      this.files = [];
      this.filesToClassify = [];
      this.selectedOrder = null;
      this.filesFormData = [];
      if (this.fileUpload) {
        this.fileUpload.clear();
      }
      this.loadFiles();

    } catch (err) {
      console.error(`Error uploading files: ${err}`);
      this.messageService.add({ severity: 'error', summary: this.translate.instant('ERROR'), detail: `Error uploading files: ${err}` });
      Swal.close();
      this.files = [];
      this.filesToClassify = [];
      this.selectedOrder = null;
      this.filesFormData = [];
      if (this.fileUpload) {
        this.fileUpload.clear();
      }
      this.loadFiles();
    }
  }

  downloadSelectedFilesAsZip() {
    if (this.selectedFiles.length === 0) {
      alert(this.translate.instant('MVF_NO_FILES_SELECTED'));
      return;
    }

    this.loading = true;
    const zip = new JSZip();
    const promises = this.selectedFiles.map((file) => {
      return fetch(file.path)
        .then(response => response.blob())
        .then(blob => zip.file(file.name, blob));
    });

    Promise.all(promises).then(() => {
      zip.generateAsync({ type: "blob" }).then(content => {
        if (this.order.webhookOrderUrl) {
          saveAs(content, `${this.order.data['Patient Data'].name}_${this.order.data['Patient Data'].surname}_${this.order.webhookOrderUrl}.zip`);
          this.loading = false
        } else {
          saveAs(content, `${this.order.data['Patient Data'].name}_${this.order.data['Patient Data'].surname}.zip`);
          this.loading = false
        }
      });
    });
  }

  canShowSelectAll(): boolean {
    return this.files.length > 0 && this.files.every(file => file.paid || file.type === 'pdf' || file.type === 'jpeg');
  }

  handleFormDataReady(formData) {
    console.log("FormData in handleFormDataReady: ", formData)
    console.log("FormData.files: ", formData.files)
    this.filesFormData = formData.files
    this.uploadFile()
  }

  handleCloseModal() {
    this.showFileClassificationModal = false
  }

  handleCloseModalWhenCancel() {
    this.showFileClassificationModal = false
    this.files = []
    this.filesToClassify = []
    this.selectedOrder = null
    this.filesFormData = [];
    if (this.fileUpload) {
      this.fileUpload.clear();
    }
  }
}
